import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { VerticalScrollableContentWithArrows } from "@/components/ui/vertical-scrollable-content-with-arrows";
import { Users, X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { DashboardItinerary } from "../models";

interface ItineraryDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  itinerary: DashboardItinerary | null;
  onViewItinerary: (itineraryId: string) => void;
  showButton?: boolean;
}

export const ItineraryDetailsModal = ({
  isOpen,
  onClose,
  itinerary,
  onViewItinerary,
  showButton = true,
}: ItineraryDetailsModalProps) => {
  const { t } = useLanguage();

  if (!itinerary) return null;

  // Calcola il numero di persone
  const guestCount = Array.isArray(itinerary.travelRequest.guestsData) ? itinerary.travelRequest.guestsData.length : 1;

  // Calcola il prezzo a persona
  const pricePerPerson = itinerary.total && guestCount > 0
    ? (
      typeof itinerary.total === 'string'
        ? parseFloat((itinerary.total ?? "0").replace(/[^0-9.-]+/g, ""))
        : itinerary.total
    ) / guestCount
    : itinerary.total;

  const formattedPricePerPerson = typeof pricePerPerson === 'number'
    ? pricePerPerson.toFixed(0)
    : pricePerPerson;

  // Estrai le età dei guest
  const guestAges = Array.isArray(itinerary.travelRequest.guestsData)
    ? itinerary.travelRequest.guestsData.map(guest => guest.age || 'N/A')
    : [];

  const handleViewItinerary = () => {
    onViewItinerary(itinerary.id);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      size="xl"
      showHeader={false}
    >
      {/* Pulsante X per chiudere */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-50 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white flex items-center justify-center transition-colors shadow-md"
      >
        <X className="h-5 w-5 text-gray-800" />
      </button>

      <div className="flex flex-col w-full h-full lg:flex-row gap-0">
        {/* Immagine a sinistra */}
        <div className="h-[200px] lg:w-[540px] lg:h-[540px] flex-shrink-0 relative">
          <div
            className="absolute inset-0 bg-gray-200 bg-cover bg-center"
            style={{
              backgroundImage: itinerary.image
                ? `url(${itinerary.image})`
                : undefined
            }}
          />
          {!itinerary.image && (
            <div className="absolute inset-0 flex items-center justify-center text-gray-400">
              <span className="text-sm">{t('itinerary.noImage')}</span>
            </div>
          )}
        </div>

        {/* Dettagli a destra */}
        <div className="w-full h-full max-h-full lg:h-[540px] lg:min-w-[400px] p-8 flex flex-col">

          <div>
            {/* Titolo */}
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {itinerary.title || t('itinerary.untitledItinerary')}
            </h2>

            {/* Date e prezzo - 3 colonne */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">{t('itinerary.from')}</p>
                <p className="text-sm font-medium text-gray-900">
                  {itinerary.travelRequest.travelStartDate
                    ? new Date(itinerary.travelRequest.travelStartDate).toLocaleDateString()
                    : '-'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">{t('itinerary.to')}</p>
                <p className="text-sm font-medium text-gray-900">
                  {itinerary.travelRequest.travelEndDate
                    ? new Date(itinerary.travelRequest.travelEndDate).toLocaleDateString()
                    : '-'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">{t('itinerary.pricePerPerson')}</p>
                <p className="text-sm font-medium text-gray-900">
                  € {formattedPricePerPerson}
                </p>
              </div>
            </div>

            {/* Guests */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Users className="h-5 w-5 text-gray-600" />
                <span className="text-sm font-semibold text-gray-900">
                  {guestCount} {guestCount === 1 ? t('itinerary.guest') : t('itinerary.guests')}
                </span>
              </div>
              {guestAges.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {guestAges.map((age, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-white border border-gray-200 text-gray-700"
                    >
                      <Users className="h-3 w-3 text-gray-400" />
                      {age} {t('itinerary.years')}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Trip Description */}
          {itinerary.description && (
            <>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                {t('itinerary.tripDescription')}
              </h4>
              <VerticalScrollableContentWithArrows className="flex-grow flex-shrink mb-6 flex-1">
                <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                  {itinerary.description}
                </p>
              </VerticalScrollableContentWithArrows>
            </>
          )}

          {/* Bottone View Itinerary */}
          {showButton && (
          <div className="mt-auto">
            <Button
              onClick={handleViewItinerary}
              className="w-full"
              size="lg"
            >
              {t('itinerary.viewItineraryProposal')}
            </Button>
          </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
